import { useCallback, useEffect, useState } from 'react';
import {
  ApiError,
  clearToken,
  authenticate,
  createDraft,
  submitProject,
  withdrawProject,
  deleteProject,
  getApiErrorInfo,
  getProjects,
  getToken,
  getMe,
} from '../../lib/api';
import { getLastRequest } from '../../lib/fetcher';
import { isApiUnavailableError } from '../../lib/apiErrors';
import { DEMO_PROJECTS, isDemoFromUrl } from '../../lib/demo';
import { getApiBaseUrl } from '../../config/api';
import { processReferralOnStart } from '../../utils/referral';
import { useAppStore } from '../../store/useAppStore';
import { useProjectsStore } from '../../store/useProjectsStore';

const BOOT_WATCHDOG_MS = 2800;

export function useMyProjectsData() {
  const apiBaseUrl = getApiBaseUrl();
  const isDemo = apiBaseUrl === null || isDemoFromUrl();
  const { addNotification, setScreen, setSelectedProjectId, setCurrentUser } = useAppStore();
  const setAuthError = useAppStore((s) => s.setAuthError);
  const { projects, setProjects } = useProjectsStore();

  const [loading, setLoading] = useState(true);
  const [initAttempt, setInitAttempt] = useState(0);
  const [bootCompleted, setBootCompleted] = useState(false);
  const [bootWatchdogFired, setBootWatchdogFired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [lastFetchErrorInfo, setLastFetchErrorInfo] = useState<ReturnType<typeof getApiErrorInfo> | null>(null);
  const [apiUnavailable, setApiUnavailable] = useState<ReturnType<typeof getApiErrorInfo> | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!loading) setBootCompleted(true);
  }, [loading]);
  useEffect(() => {
    if (bootCompleted || !loading) {
      setBootWatchdogFired(false);
      return;
    }
    const t = window.setTimeout(() => setBootWatchdogFired(true), BOOT_WATCHDOG_MS);
    return () => window.clearTimeout(t);
  }, [bootCompleted, initAttempt, loading]);

  const handleApiFailure = useCallback((err: unknown, fallback: string) => {
    const info = getApiErrorInfo(err);
    if (isApiUnavailableError(info)) {
      setApiUnavailable(info);
      setError(null);
      setErrorDetails(null);
      setLastFetchErrorInfo(null);
      return;
    }
    setApiUnavailable(null);
    setLastFetchErrorInfo(info);
    const e = err instanceof Error ? err : new Error(String(err));
    setError(`Network error: ${e.name} ${e.message || info.message || fallback}`);
    setErrorDetails(
      JSON.stringify(
        {
          status: info.status ?? null,
          message: info.message || fallback,
          request: info.request ?? null,
          error_report_id: info.errorReportRequestId ?? null,
          stack: e.stack ?? null,
          lastRequest: getLastRequest(),
        },
        null,
        2
      )
    );
  }, []);

  const tryAuth = useCallback(async () => {
    const tg = (window as { Telegram?: { WebApp?: { initData?: string } } }).Telegram?.WebApp;
    const initData = tg?.initData;
    if (!initData)
      throw new ApiError({
        kind: 'unknown',
        code: 'INIT_DATA_MISSING',
        message: 'Telegram initData не найден. Откройте Mini App из Telegram.',
      });
    await authenticate(initData);
    await processReferralOnStart();
    setAuthError(null);
  }, [setAuthError]);

  const loadProjects = useCallback(async () => {
    setInitAttempt((v) => v + 1);
    setBootWatchdogFired(false);
    if (isDemo) {
      setProjects(DEMO_PROJECTS);
      setApiUnavailable(null);
      setError(null);
      setErrorDetails(null);
      setLastFetchErrorInfo(null);
      setAuthError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setErrorDetails(null);
    setLastFetchErrorInfo(null);
    setApiUnavailable(null);
    try {
      const tg = (window as { Telegram?: { WebApp?: { initData?: string } } }).Telegram?.WebApp;
      if (tg?.initData) {
        try {
          await tryAuth();
        } catch (authErr) {
          setAuthError(authErr instanceof Error ? authErr.message : 'Ошибка входа');
          setProjects([]);
          setLoading(false);
          return;
        }
      } else if (!getToken()) {
        setProjects([]);
        setLoading(false);
        return;
      }
      if (!getToken()) {
        setProjects([]);
        setLoading(false);
        return;
      }
      const data = await getProjects();
      setProjects(data);
      setAuthError(null);
      setLastFetchErrorInfo(null);
      getMe().then((me) => setCurrentUser(me)).catch(() => setCurrentUser(null));
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'http' && err.status === 401) {
        clearToken();
        try {
          await tryAuth();
          const retriedData = await getProjects();
          setProjects(retriedData);
          setAuthError(null);
          setError(null);
          setErrorDetails(null);
          setLastFetchErrorInfo(null);
          setApiUnavailable(null);
          getMe().then((me) => setCurrentUser(me)).catch(() => setCurrentUser(null));
          setLoading(false);
          return;
        } catch (retryErr) {
          const retryInfo = getApiErrorInfo(retryErr);
          if (isApiUnavailableError(retryInfo)) {
            setApiUnavailable(retryInfo);
            setError(null);
            setErrorDetails(null);
            setLastFetchErrorInfo(null);
            setAuthError(null);
          } else {
            setApiUnavailable(null);
            setLastFetchErrorInfo(retryInfo);
            setAuthError(retryInfo.message);
            const e = retryErr instanceof Error ? retryErr : new Error(String(retryErr));
            setError(`Network error: ${e.name} ${e.message || retryInfo.message || 'Request failed'}`);
            setErrorDetails(
              JSON.stringify(
                {
                  status: retryInfo.status ?? null,
                  message: retryInfo.message ?? null,
                  request: retryInfo.request ?? null,
                  error_report_id: retryInfo.errorReportRequestId ?? null,
                  stack: e.stack ?? null,
                  lastRequest: getLastRequest(),
                },
                null,
                2
              )
            );
          }
          setProjects([]);
          setLoading(false);
          return;
        }
      }
      handleApiFailure(err, 'Не удалось загрузить проекты');
      const errInfo = getApiErrorInfo(err);
      if (!isApiUnavailableError(errInfo) && errInfo.message) setAuthError(errInfo.message);
      else setAuthError(null);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [handleApiFailure, isDemo, tryAuth, setProjects, setCurrentUser, setAuthError]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleOpen = useCallback(
    (id: string) => {
      if (isDemo || apiUnavailable) return;
      setScreen('detail');
      setSelectedProjectId(id);
      useAppStore.getState().setDetailSource('my');
    },
    [apiUnavailable, isDemo, setScreen, setSelectedProjectId]
  );

  const handleEdit = useCallback(
    (id: string) => {
      if (isDemo || apiUnavailable) return;
      setScreen('wizard');
      setSelectedProjectId(id);
    },
    [apiUnavailable, isDemo, setScreen, setSelectedProjectId]
  );

  const handleSubmit = useCallback(
    async (id: string) => {
      if (isDemo || apiUnavailable) return;
      const proj = projects.find((p) => p.id === id);
      if (!proj?.can_submit) {
        addNotification('Заполните все обязательные поля проекта');
        setScreen('wizard');
        setSelectedProjectId(id);
        return;
      }
      const showContacts = Boolean(proj?.show_contacts);
      try {
        if (!getToken()) await tryAuth();
        await submitProject(id, { show_contacts: showContacts });
        addNotification('Проект отправлен на модерацию');
        await loadProjects();
      } catch (err) {
        const info = getApiErrorInfo(err);
        if (isApiUnavailableError(info)) setApiUnavailable(info);
        else if (info.status === 429)
          addNotification('Вы уже публиковали проект недавно. Подождите 10 минут.');
        else if (info.message && String(info.message).includes('Missing required')) {
          addNotification('Заполните все обязательные поля в форме');
          setScreen('wizard');
          setSelectedProjectId(id);
        } else handleApiFailure(err, 'Не удалось отправить на модерацию');
      }
    },
    [apiUnavailable, handleApiFailure, isDemo, loadProjects, projects, tryAuth, addNotification, setScreen, setSelectedProjectId]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (isDemo || apiUnavailable) return;
      if (!window.confirm('Удалить проект?')) return;
      try {
        if (!getToken()) await tryAuth();
        await deleteProject(id);
        addNotification('Проект удалён');
        await loadProjects();
      } catch (err) {
        const info = getApiErrorInfo(err);
        if (isApiUnavailableError(info)) setApiUnavailable(info);
        else handleApiFailure(err, 'Не удалось удалить проект');
      }
    },
    [apiUnavailable, handleApiFailure, isDemo, loadProjects, tryAuth, addNotification]
  );

  const handleWithdraw = useCallback(
    async (id: string) => {
      if (isDemo || apiUnavailable) return;
      try {
        if (!getToken()) await tryAuth();
        await withdrawProject(id);
        addNotification('Проект возвращён в черновик');
        await loadProjects();
      } catch (err) {
        const info = getApiErrorInfo(err);
        if (isApiUnavailableError(info)) setApiUnavailable(info);
        else handleApiFailure(err, 'Не удалось вернуть проект в черновик');
      }
    },
    [apiUnavailable, handleApiFailure, isDemo, loadProjects, tryAuth, addNotification]
  );

  const createProjectAndOpenWizard = useCallback(async () => {
    if (isDemo) {
      addNotification('Создание проекта будет доступно после подключения API');
      return;
    }
    const tg = (window as { Telegram?: { WebApp?: { initData?: string } } }).Telegram?.WebApp;
    const initData = tg?.initData;

    if (!getToken()) {
      if (!initData) {
        addNotification('Откройте приложение из Telegram — без этого нельзя создать проект.');
        return;
      }
      try {
        await authenticate(initData);
      } catch (authErr) {
        addNotification('Не удалось войти. Откройте приложение заново из Telegram.');
        setAuthError(authErr instanceof Error ? authErr.message : 'Ошибка входа');
        return;
      }
    }

    setCreating(true);
    try {
      let created = await createDraft();
      setScreen('wizard');
      setSelectedProjectId(created.id);
      addNotification(`Проект создан`);
      await loadProjects();
    } catch (err) {
      const info = getApiErrorInfo(err);
      if (isApiUnavailableError(info)) {
        addNotification('API недоступен. Попробуйте позже.');
      } else if (info.status === 401) {
        clearToken();
        if (initData) {
          try {
            await authenticate(initData);
            const created = await createDraft();
            setScreen('wizard');
            setSelectedProjectId(created.id);
            addNotification(`Проект создан`);
            await loadProjects();
          } catch (retryErr) {
            const retryInfo = getApiErrorInfo(retryErr);
            addNotification('Сессия истекла. Откройте приложение заново из Telegram.');
            setAuthError(retryInfo.message || 'Требуется вход');
          }
        } else {
          addNotification('Сессия истекла. Откройте приложение заново из Telegram.');
          setAuthError(info.message || 'Требуется вход');
        }
      } else {
        const detail = info.message ? `: ${info.message}` : '';
        addNotification(`Не удалось создать проект${detail}`);
      }
    } finally {
      setCreating(false);
    }
  }, [isDemo, setScreen, setSelectedProjectId, addNotification, loadProjects, setAuthError]);

  return {
    apiBaseUrl,
    isDemo,
    projects,
    loading,
    bootWatchdogFired,
    error,
    errorDetails,
    lastFetchErrorInfo,
    apiUnavailable,
    loadProjects,
    handleOpen,
    handleEdit,
    handleSubmit,
    handleWithdraw,
    handleDelete,
    createProjectAndOpenWizard,
    creating,
  };
}
